import { useFormContext } from "react-hook-form";

import { ControlledWrapper } from "@krea/shared/design-system/input/controlled-wrapper";
import { FieldLabel } from "@krea/shared/design-system/input/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupText,
} from "@krea/shared/design-system/input/input-group";
import { IntegerInput } from "@krea/shared/design-system/input/integer-input";
import { MoneyInput } from "@krea/shared/design-system/input/money-input";
import { PercentageInput } from "@krea/shared/design-system/input/percentage-input";
import { Label } from "@krea/shared/design-system/Label";
import {
  RadioGroup,
  RadioGroupItem,
} from "@krea/shared/design-system/RadioGroup";
import type { Currency } from "@krea/shared/types/common";

import { LeasingBidTypeEnum } from "@/features/common/enums";
import type { ILeasingBidInput } from "@/features/lender-responses/types/lender-response";
import type { BidPostData } from "@/validations";

import DetailsItem from "../../../../details/item";
import {
  AdministrationFeeTooltip,
  administrationFeeLabelText,
} from "../../bidHelpers";

function TypeOfContractRadioGroup({
  name,
  value,
  onChange,
  isSubmitting,
}: {
  name: string;
  value: string;
  onChange: (v: string) => void;
  isSubmitting: boolean;
}) {
  const leasingId = name;
  const rentId = `${name}-rent`;

  return (
    <RadioGroup
      onValueChange={onChange}
      value={value}
      disabled={isSubmitting}
      className="flex flex-row gap-4"
    >
      <div className="flex items-center gap-2">
        <RadioGroupItem value={LeasingBidTypeEnum.LEASING} id={leasingId} />
        <Label htmlFor={leasingId} className="cursor-pointer font-normal">
          Leasing
        </Label>
      </div>
      <div className="flex items-center gap-2">
        <RadioGroupItem value={LeasingBidTypeEnum.RENT} id={rentId} />
        <Label htmlFor={rentId} className="cursor-pointer font-normal">
          Rent
        </Label>
      </div>
    </RadioGroup>
  );
}

interface IFormProps {
  bidFormIndex: number;
  currency: Currency;
  isEditing: boolean; // Is editing existing offer or creating new
  isInterestRatePerYearActivated?: boolean;
}

export const LeasingForm = ({
  bidFormIndex,
  currency,
  isEditing,
}: IFormProps) => {
  const {
    control,
    formState: { isSubmitting },
  } = useFormContext<BidPostData<ILeasingBidInput>>();

  return (
    <div className="flex w-full flex-col">
      <div className="w-full space-y-4">
        <DetailsItem
          title={isEditing ? "Update leasing terms" : "Leasing terms"}
          className="mb-4"
        >
          <div className="w-full space-y-4">
            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.amount`}
              label="Amount"
              required
              render={({ field }) => (
                <MoneyInput {...field} currency={currency} id={field.name} />
              )}
            />

            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.downPayment`}
              label="Down payment"
              required
              render={({ field }) => (
                <MoneyInput {...field} currency={currency} id={field.name} />
              )}
            />

            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.paybackTimeMonths`}
              label="Repayment time"
              required
              render={({ field }) => (
                <InputGroup>
                  <IntegerInput
                    {...field}
                    id={field.name}
                    className="flex-1 rounded-none border-0 bg-transparent shadow-none ring-0 focus-visible:ring-0 disabled:bg-transparent aria-invalid:ring-0"
                  />
                  <InputGroupAddon align="inline-end">
                    <InputGroupText>months</InputGroupText>
                  </InputGroupAddon>
                </InputGroup>
              )}
            />

            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.monthlyPayment`}
              label="Monthly payment"
              required
              render={({ field }) => (
                <MoneyInput {...field} currency={currency} id={field.name} />
              )}
            />

            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.terminalValuePercentage`}
              label="Terminal value"
              required
              render={({ field }) => (
                <PercentageInput
                  {...field}
                  id={field.name}
                  placeholder='Value in "%"'
                />
              )}
            />

            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.contractType`}
              label="Type of contract"
              required
              render={({ field }) => (
                <TypeOfContractRadioGroup
                  name={field.name}
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  isSubmitting={isSubmitting}
                />
              )}
            />

            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.setupFee`}
              label="Setup fee"
              render={({ field }) => (
                <MoneyInput {...field} currency={currency} id={field.name} />
              )}
            />
          </div>
        </DetailsItem>

        <DetailsItem title="" className="mb-4">
          <div className="w-full space-y-4">
            <span className="mb-2 ml-3 block text-sm italic text-gray-600">
              Here you can explain other costs & qualitative information about
              your offer:
            </span>

            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.insuranceMonthlyCost`}
              label="Insurance cost (per month)"
              render={({ field }) => (
                <MoneyInput {...field} currency={currency} id={field.name} />
              )}
            />

            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.administrationFees`}
              render={({ field }) => (
                <>
                  <div className="flex items-center gap-2">
                    <FieldLabel>{administrationFeeLabelText}</FieldLabel>
                    <AdministrationFeeTooltip />
                  </div>
                  <MoneyInput {...field} currency={currency} id={field.name} />
                </>
              )}
            />
          </div>
        </DetailsItem>
      </div>
    </div>
  );
};
